import { useEffect, useState } from "react";
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { Helmet } from "react-helmet";

type Row = {
    userId: string;
    status: string;
    bookingDate: string;
    stripePaymentReference: string;
    address: {
        addressLine1: string;
        addressLine2: string;
        city: string;
        postCode: string;
        name: string;
    },
    meta: {
        cardBorderColour: string;
        cardTextColour: string;
    }
}

type User = {
    id: string;
    email: string;
    name: string;
    hasPaid: boolean;
}

type Stock = {
    'card-holders': number;
    envelopes: number;
    'id-cards': number;
    'printer-ribbons': number;
    stamops: number;
    'thermal-labels': number;
}


export default function AdminDashboard() {
    const [orders, setOrders] = useState<Row[]>([]);
    const [users, setUsers] = useState<User[]>([]);
    const [stock, setStock] = useState<Stock>({} as Stock);

    useEffect(() => {
        const fetchDashboard = async () => {
            try {
                const ordersRes = await fetch(`${import.meta.env.VITE_API_URL}/api/get-orders`, {
                    credentials: "include",
                });

                const usersRes = await fetch(`${import.meta.env.VITE_API_URL}/api/get-users`, {
                    credentials: "include",
                });

                const stockRes = await fetch(`${import.meta.env.VITE_API_URL}/api/get-stock`, {
                    credentials: "include",
                });

                if (!ordersRes.ok || !usersRes.ok || !stockRes.ok) throw new Error("Not authorized");
                const ordersData = await ordersRes.json();
                setOrders(ordersData);
                const usersData = await usersRes.json();
                setUsers(usersData);
                const stockData = await stockRes.json();
                setStock(stockData);
            } catch {
                console.error("Failed to fetch orders");
            }
        };

        fetchDashboard();
    }, []);

    const handleStatusUpdate = async (userId: string, status: string) => {
        try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/update-order-status`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    userId,
                    status,
                }),
            });

            if (!res.ok) throw new Error("Failed to update status");

            // Update the order in state
            setOrders((prev: Row[]) =>
                prev.map((order: Row) =>
                    order.userId === userId ? { ...order, status } : order
                )
            );
        } catch (err) {
            console.error(err);
        }
    };

    const statusMap: Record<string, string> = {
        ['complete']: 'Complete',
        ['not-complete']: 'Not Complete',
    }

    const orderColumns = [
        { field: 'userId', headerName: 'User ID', width: 300 },
        { field: 'status', headerName: 'Status', width: 150, renderCell: ({ row }: { row: Row }) => <div>{statusMap[row.status]}</div> },
        {
            field: 'borderColour',
            headerName: 'Border Colour',
            width: 200,
            renderCell: ({ row }: { row: Row }) => <div>{row.meta.cardBorderColour}</div>
        },
        {
            field: 'textColour',
            headerName: 'Text Colour',
            width: 200,
            renderCell: ({ row }: { row: Row }) => <div>{row.meta.cardTextColour === '#FFFFFF' ? 'White' : 'Black'}</div>
        },
        { field: 'bookingDate', headerName: 'Booking Date', width: 200, renderCell: ({ row }: { row: Row }) => <div>{new Date(row.bookingDate).toLocaleString('en-GB')}</div> },
        { field: 'stripePaymentReference', headerName: 'Stripe Payment Reference', width: 300 },
        {
            field: 'name',
            headerName: 'Name',
            width: 200,
            renderCell: ({ row }: { row: Row }) => <div>{row.address.name}</div>
        },
        {
            field: 'address', headerName: 'Address', width: 400, renderCell: ({ row }: { row: Row }) => <div className="whitespace-normal ">      {Object.entries(row.address)
                .filter(([key, value]) => key !== 'name' && value !== '')
                .map(([, value]) => value)
                .join(', ')}</div>
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 200,
            renderCell: ({ row }: { row: Row }) => {
                const { status, userId } = row;
                const isComplete = status === 'complete';

                return (
                    <div className="flex items-center justify-start w-full h-full">
                        <button
                            className={`px-3 py-1 rounded text-lg font-medium border bg-blue-300 border-black hover:bg-blue-400`}
                            onClick={() => handleStatusUpdate(userId, status === 'complete' ? 'not-complete' : 'complete')}
                        >
                            Mark {isComplete ? 'Not Complete' : 'Complete'}
                        </button>
                    </div>
                );
            }
        }
    ];

    const userColumns = [
        { field: 'id', headerName: 'User ID', width: 400 },
        { field: 'email', headerName: 'Email', width: 400 },
        { field: 'name', headerName: 'Name', width: 300 },
        { field: 'hasPaid', headerName: 'Has Paid?', width: 200 },
    ];

    const renderCell = (params: GridRenderCellParams<Stock>) => {
        const value = params.value;
        let bgColor = '';
        if (value < 50) bgColor = '#fee2e2';
        else if (value < 125) bgColor = '#ffedd5';
        else bgColor = '#d1fae5';

        return (
            <div
                style={{
                    backgroundColor: bgColor,
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                {value}
            </div>
        );
    };

    const stockColumns = [
        {
            field: 'card-holders', headerName: 'Card Holders', width: 200, renderCell: (params: GridRenderCellParams<Stock>) => renderCell(params)
        },
        { field: 'envelopes', headerName: 'Envelopes', width: 200, renderCell: (params: GridRenderCellParams<Stock>) => renderCell(params) },
        { field: 'id-cards', headerName: 'ID Cards', width: 200, renderCell: (params: GridRenderCellParams<Stock>) => renderCell(params) },
        { field: 'printer-ribbons', headerName: 'Printer Ribbons', width: 200, renderCell: (params: GridRenderCellParams<Stock>) => renderCell(params) },
        { field: 'stamps', headerName: 'Stamps', width: 200, renderCell: (params: GridRenderCellParams<Stock>) => renderCell(params) },
        { field: 'thermal-labels', headerName: 'Thermal Labels', width: 200, renderCell: (params: GridRenderCellParams<Stock>) => renderCell(params) },
    ];

    const logout = async () => {
        await fetch(`${import.meta.env.VITE_API_URL}/api/admin-logout`, {
            method: "POST",
            credentials: "include",
        });
        window.location.href = "/";
    };

    return (
        <>
            <Helmet>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <div className="flex flex-col gap-14">
                <div className="p-4 mx-auto w-full">
                    <h1 className="text-2xl font-bold mb-4">Orders</h1>
                    <DataGrid initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                                page: 0,
                            },
                        },
                    }} pageSizeOptions={[10, 25, 50]} disableRowSelectionOnClick={true} sortModel={[{ field: 'bookingDate', sort: 'asc' }]} columns={orderColumns} rowHeight={75} rows={orders.map((row: Row) => ({ ...row, id: row.userId }))} getRowClassName={(params) => params.row.status === 'complete' ? 'bg-green-100' : 'bg-red-100'} sx={{
                        '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeader:focus': {
                            outline: 'none',
                        },
                    }} />
                </div>
                <div className="p-4 mx-auto w-full">
                    <h1 className="text-2xl font-bold mb-4">Users</h1>
                    <DataGrid initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                                page: 0,
                            },
                        },
                    }} pageSizeOptions={[10, 25, 50]} disableRowSelectionOnClick={true} columns={userColumns} rowHeight={75} rows={users} getRowClassName={(params) => params.row.hasPaid ? 'bg-green-100' : 'bg-red-100'} sx={{
                        '& .MuiDataGrid-cell:focus': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeader:focus': {
                            outline: 'none',
                        },
                    }} />
                </div>
                <div className="p-4 mx-auto w-full">
                    <h1 className="text-2xl font-bold mb-4">Stock</h1>
                    <DataGrid disableRowSelectionOnClick={true} columns={stockColumns} rowHeight={75} rows={[{ ...stock, id: '123' }]} sx={{
                        '& .MuiDataGrid-cell': {
                            padding: 0,
                            margin: 0,
                        },
                    }} />
                    <button onClick={logout} className="mt-4 bg-red-600 text-white px-4 py-2">
                        Logout
                    </button>
                </div>
            </div>
        </>
    );
}
